import { useAuth } from "@providers/auth";
import React, { useMemo } from "react";
import { matchPath } from "react-router-dom";

import { SidebarItemsType } from "@shared/theme/types/sidebar";

import SidebarNavListItem from "./SidebarNavListItem";

interface ReduceChildRoutesProps {
  currentRoute: string;
  depth: number;
  items: React.ReactElement[];
  page: SidebarItemsType;
}

const reduceChildRoutes = (props: ReduceChildRoutesProps) => {
  const { items, page, depth, currentRoute } = props;
  if (page.children) {
    const open = page.href
      ? !!matchPath(
          {
            path: page.href,
            end: false,
          },
          currentRoute
        )
      : false;

    items.push(
      <SidebarNavListItem
        depth={depth}
        icon={page.icon}
        key={page.title}
        badge={page.badge}
        open={!!open}
        title={page.title}
        href={page.href}
      >
        <SidebarNavList depth={depth + 1} pages={page.children} />
      </SidebarNavListItem>
    );
  } else {
    items.push(
      <SidebarNavListItem
        depth={depth}
        href={page.href}
        icon={page.icon}
        key={page.title}
        badge={page.badge}
        title={page.title}
        newTab={page.newTab}
      />
    );
  }

  return items;
};

interface SidebarNavListProps {
  depth: number;
  pages: SidebarItemsType[];
}

const SidebarNavList: React.FC<SidebarNavListProps> = (props) => {
  const { pages, depth } = props;

  const { permissions, isLoading } = useAuth();

  if (import.meta.env.DEV) {
    (window as unknown as { Permissions?: string[] | undefined }).Permissions = permissions;
  }

  // const router = useLocation();
  const currentRoute = window.location.pathname;

  const childRoutes = useMemo(() => {
    const authorizedPages = pages.filter((page) => {
      if (page.scope) {
        if (isLoading) {
          return false;
        }
        const list = Array.isArray(permissions) ? permissions : [];
        return page.scope
          .split(" ")
          .filter((s) => !!s)
          .every((s) => list.includes(s));
      }
      return true;
    });
    return authorizedPages.reduce(
      (items, page) => reduceChildRoutes({ items, page, currentRoute, depth }),
      [] as React.ReactElement[]
    );
  }, [JSON.stringify(pages), currentRoute, depth, permissions?.join(","), isLoading]);

  return <React.Fragment>{childRoutes}</React.Fragment>;
};

export default SidebarNavList;
